import React, { useCallback, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import TrapFocus from "@mui/material/Unstable_TrapFocus";

export type DesignDialogIcon =
  "success" | "trash" | "question" | "lock" | "warning" | "warningTriangle" | "pause" | "email";
export type DesignDialogButtonProps =
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    tone?: "gold" | "dark" | "red" | "green" | "blue";
  };
const buttonColors = {
  gold: "#E1A731",
  dark: "#26282C",
  red: "#F21B1B",
  green: "#10B981",
  blue: "#2196F3",
};

export const DesignDialogButton = ({
  tone = "gold",
  className = "",
  style,
  children,
  ...props
}: DesignDialogButtonProps) => (
  <button
    type="button"
    {...props}
    className={`inline-flex min-w-0 flex-1 items-center justify-center gap-[8px] rounded-[8px] border-0 px-[20px] py-[12px] text-[14px] font-semibold leading-[20px] text-white transition hover:brightness-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cpaGold disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    style={{ backgroundColor: buttonColors[tone], ...style }}
  >
    {children}
  </button>
);

export const DesignDialogGlyph = ({ icon }: { icon: DesignDialogIcon }) => (
  <svg
    aria-hidden="true"
    width="58%"
    height="58%"
    viewBox="0 0 48 48"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {icon === "success" && (
      <>
        <circle cx="24" cy="24" r="18" />
        <path d="m15 24 6 6 12-13" />
      </>
    )}
    {icon === "trash" && (
      <>
        <path d="M12 15h24M19 15V9h10v6M15 15l2 24h14l2-24M21 21v12M27 21v12" />
      </>
    )}
    {icon === "question" && (
      <>
        <circle cx="24" cy="24" r="18" />
        <path d="M18 18a6 6 0 0 1 12 0c0 4-6 5-6 9M24 33h.01" />
      </>
    )}
    {icon === "warning" && (
      <>
        <circle cx="24" cy="24" r="18" />
        <path d="M24 13v14M24 34h.01" />
      </>
    )}
    {/* Design (16) UNSAVED CHANGES: white warning triangle in the red circle. */}
    {icon === "warningTriangle" && (
      <>
        <path d="M20.6 7.8 3.6 36a4 4 0 0 0 3.4 6h34a4 4 0 0 0 3.4-6L27.4 7.8a4 4 0 0 0-6.8 0Z" />
        <path d="M24 18v8M24 34h.01" />
      </>
    )}
    {icon === "lock" && (
      <>
        <rect x="12" y="21" width="24" height="18" rx="3" />
        <path d="M17 21v-6a7 7 0 0 1 14 0v6M24 28v5" />
      </>
    )}
    {icon === "pause" && (
      <>
        <circle cx="24" cy="24" r="18" />
        <path d="M19 17v14M29 17v14" />
      </>
    )}
    {icon === "email" && (
      <>
        <rect x="7" y="12" width="34" height="25" rx="3" />
        <path d="m8 15 16 12 16-12" />
      </>
    )}
  </svg>
);

/** Bolds each highlight inside a string message, as the design pop-ups do. */
export const renderHighlightedMessage = (
  message: React.ReactNode,
  highlights: readonly string[] = [],
): React.ReactNode => {
  const highlightPattern = highlights
    .filter(Boolean)
    .slice()
    .sort((left, right) => right.length - left.length)
    .map((value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|");
  if (typeof message !== "string" || !highlightPattern) return message;
  return message
    .split(new RegExp(`(${highlightPattern})`, "g"))
    .map((part, index) =>
      highlights.includes(part) ? <strong key={index}>{part}</strong> : part,
    );
};

// M5: each open dialog has a focus trap. When two are open, both traps pull
// focus back and the browser hangs. Only the most recently opened dialog
// enforces its trap and handles Escape. A parent renders before its nested
// dialog, so render order gives the nested dialog the higher position.
let designDialogOpenSequence = 0;
const openDesignDialogs = new Set<number>();
const isTopDesignDialog = (position: number | null) =>
  position !== null && position === Math.max(...Array.from(openDesignDialogs));

export type DesignDialogProps = {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  message?: React.ReactNode;
  highlights?: readonly string[];
  /** Shown between the title and the message (CMP Dashboard (24) CD:7164 delete warning). */
  beforeMessage?: React.ReactNode;
  note?: React.ReactNode;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  size?: "default" | "wide" | "ida";
  icon?: DesignDialogIcon;
  /** Custom glyph rendered inside the standard 64px circle instead of `icon`. */
  iconNode?: React.ReactNode;
  iconColor?: string;
  busy?: boolean;
  disableBackdropClose?: boolean;
  /** Hides the red close button; Escape still calls onClose. */
  hideCloseButton?: boolean;
  closeLabel?: string;
  className?: string;
  variant?: "default" | "unsaved";
  ariaLabel?: string;
  /** Overrides the panel padding and border colour when a design pop-up differs. */
  panelStyle?: Pick<React.CSSProperties, "padding" | "borderColor" | "maxWidth">;
};

export const DesignDialog = ({
  open,
  onClose,
  title,
  message,
  highlights = [],
  beforeMessage,
  note,
  children,
  actions,
  size = "default",
  icon,
  iconNode,
  iconColor,
  busy = false,
  disableBackdropClose = false,
  hideCloseButton = false,
  closeLabel = "Close",
  className = "",
  variant = "default",
  ariaLabel,
  panelStyle,
}: DesignDialogProps) => {
  const titleId = useId();
  const messageId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const openPosition = useRef<number | null>(null);
  if (open && openPosition.current === null) {
    designDialogOpenSequence += 1;
    openPosition.current = designDialogOpenSequence;
  } else if (!open) openPosition.current = null;
  useEffect(() => {
    const position = openPosition.current;
    if (!open || position === null) return;
    openDesignDialogs.add(position);
    return () => { openDesignDialogs.delete(position); };
  }, [open]);
  const isTopDialog = useCallback(() => isTopDesignDialog(openPosition.current), []);
  const focusInitialButton = useCallback(() => {
    const dialog = dialogRef.current;
    if (!open || busy || !dialog || !isTopDialog()) return;
    const activeElement = dialog.ownerDocument.activeElement;
    if (activeElement !== dialog && dialog.contains(activeElement)) return;

    const firstAction = variant === "unsaved"
      ? actionsRef.current?.querySelector<HTMLButtonElement>("button:not(:disabled)")
      : null;
    const initialButton = firstAction ||
      dialog.querySelector<HTMLButtonElement>("button:not(:disabled)");
    initialButton?.focus({ preventScroll: true });
  }, [open, busy, variant, isTopDialog]);
  useEffect(focusInitialButton, [focusInitialButton]);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy && isTopDialog()) {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, busy, onClose, isTopDialog]);

  if (!open || typeof document === "undefined") return null;
  const isUnsaved = variant === "unsaved";
  const defaultIconColor =
    icon === "success" || icon === "pause"
      ? "#12B981"
      : icon === "lock"
        ? "#23272F"
        : icon === "email"
          ? "#2196F3"
          : "#F21B1B";
  const messageContent = renderHighlightedMessage(message, highlights);
  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center overflow-y-auto"
      style={{
        background: "rgba(10,10,10,.45)",
        padding: "clamp(12px, 4vw, 24px)",
        fontFamily: '"DM Sans", sans-serif',
      }}
      onMouseDown={(event) => {
        if (
          event.target === event.currentTarget &&
          !busy &&
          !disableBackdropClose
        ) {
          event.preventDefault();
          onClose();
        }
      }}
    >
      <TrapFocus open isEnabled={isTopDialog}>
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-busy={busy || undefined}
          aria-labelledby={title ? titleId : undefined}
          aria-describedby={message ? messageId : undefined}
          aria-label={!title ? ariaLabel || "Confirmation" : undefined}
          tabIndex={-1}
          onFocus={(event) => {
            if (event.target === event.currentTarget) focusInitialButton();
          }}
          className={`relative my-auto flex w-full flex-col items-center overflow-y-auto bg-white text-center text-[#0A0A0A] outline-none ${className}`}
          style={{
            // CMP Dashboard (16) UNSAVED CHANGES pop-ups: width min(520px, 92vw).
            maxWidth: size === "wide" ? 700 : size === "ida" ? 560 : isUnsaved ? 520 : 440,
            maxHeight: "calc(100dvh - 48px)",
            border: size === "ida" ? "2px solid #E1A731" : "1px solid #E5E7EB",
            borderRadius: size === "ida" ? 16 : 14,
            boxShadow: "0 18px 48px rgba(0,0,0,.18)",
            padding: "30px 28px",
            ...panelStyle,
          }}
        >
          {!hideCloseButton && <button
            type="button"
            autoFocus={!isUnsaved}
            aria-label={closeLabel}
            disabled={busy}
            onClick={onClose}
            className="absolute right-[14px] top-[14px] flex h-[28px] w-[28px] items-center justify-center rounded-full border-0 bg-[#F21B1B] text-white hover:brightness-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cpaGold disabled:opacity-50"
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="m3 3 10 10M13 3 3 13" />
            </svg>
          </button>}
          {(icon || iconNode) && (
            <div
              className={`flex ${isUnsaved ? "h-[72px] w-[72px]" : "h-[64px] w-[64px]"} shrink-0 items-center justify-center rounded-full text-white [&_svg]:max-h-[58%] [&_svg]:max-w-[58%]`}
              style={{ backgroundColor: iconColor || defaultIconColor }}
            >
              {iconNode ?? (icon ? <DesignDialogGlyph icon={icon} /> : null)}
            </div>
          )}
          {title && (
            <h2
              id={titleId}
              className={isUnsaved ? "text-[16px] leading-[1.4]" : "text-[14px] leading-[1.4]"}
              style={{
                fontWeight: isUnsaved ? 800 : 600,
                marginTop: icon || iconNode ? 18 : 0,
                letterSpacing: isUnsaved ? ".02em" : undefined,
              }}
            >
              {title}
            </h2>
          )}
          {beforeMessage}
          {message && (
            <div
              id={messageId}
              className={`w-full ${isUnsaved ? "leading-[1.5]" : "max-w-[340px] leading-[1.55]"} text-[14px] font-normal [&_b]:font-semibold [&_strong]:font-semibold`}
              style={{
                marginTop: title ? 8 : icon || iconNode ? 18 : 0,
                textWrap: "pretty",
              }}
            >
              {messageContent}
            </div>
          )}
          {note && (
            <div className="mt-[10px] w-full text-[14px] font-normal leading-[1.55]">
              {note}
            </div>
          )}
          {children}
          {actions && (
            <div
              ref={actionsRef}
              className={`flex w-full justify-center ${isUnsaved ? "mt-[22px] gap-[14px] [&>button]:max-w-[180px] [&>button]:rounded-[10px] [&>button]:py-[13px] [&>button]:font-bold" : "mt-[22px] gap-[12px] [&>button]:max-w-[170px]"}`}
            >
              {actions}
            </div>
          )}
        </div>
      </TrapFocus>
    </div>,
    document.body,
  );
};

export default DesignDialog;
